import { type API } from "@/api/api";
import type { RouteLocationNormalizedLoaded } from "vue-router";
import type {
  Trip,
  TripOrder,
  TripOrderFreight,
  TripOrderStop,
  TripRoute,
  TripRouteFreight,
  TripRouteStop,
  TripStop,
} from "@/types/trip";
import type { TruckDto } from "@/api/trucks/dto/truck.dto";
import {
  NotStartedRouteStopStatus,
  RouteRedispatchState,
  RouteStatus,
  RouteStopState,
  RouteStopStatus,
  TravelOrderCancellationStatusMap,
  TravelOrderStatus,
} from "@/constants/trip";
import type { RouteStopDto, TravelOrderDto, TripDto, TripRouteDto, TripStopDto } from "@/api/trips/dto/trip.dto";
import { OrderStopStatus, OrderStopType } from "@/constants/order";
import type { OrderDto, OrderFreightDto } from "@/api/orders/dto/order.dto";
import { useFreights, useOrder, useTrip } from "@/composables";
import { getEntityKey, isSameEntity } from "@/utils/sameEntity";
import { MandatoryPaymentTypes, TravelOrderPaymentStatus, TravelOrderPaymentType } from "@/constants/travelOrder";
import { useSnackbarStore } from "@/store/snackbar.store";
import type { FacilityDto } from "@/api/company/dto/facility.dto";
import { generateId } from "@/utils/generateId";
import { getFreightOrdinalLabel } from "@/utils/freightOrdinals";

const { isReloadStop, createTOName, createStopName } = useOrder();
const { createStopNumber, checkIsRouteStopProcessStarted, isTravelOrderCancelled, isRouteStopActive } = useTrip();
const { getFreightName, createRouteFreightFromOrderFreight, createFreightState } = useFreights();

class TripManager {
  trip: Trip = { orders: [], routes: [], tripStops: [] };
  originalTrip: TripDto | null = null;
  private api: API;
  private route: RouteLocationNormalizedLoaded;
  private newTripSlug = "new";

  constructor(api: API, route: RouteLocationNormalizedLoaded) {
    this.api = api;
    this.route = route;
  }

  get orders(): TripOrder[] {
    return this.trip.orders;
  }

  get routes(): TripRoute[] {
    return this.trip.routes;
  }

  get activeRoutes(): TripRoute[] {
    return this.trip.routes.filter((route) => route.status !== RouteStatus.cancelled);
  }

  get tripStops(): TripStop[] {
    return [...this.trip.tripStops].sort((a, b) => a.position - b.position);
  }

  get orderStops(): TripOrderStop[] {
    return this.orders.flatMap((order) => order.orderStops);
  }

  get tripStopsById(): Map<string, TripStop> {
    return new Map(this.tripStops.map((tripStop) => [tripStop.id, tripStop]));
  }

  get tripStopsByOrderStopId(): Map<string | null, TripStop> {
    const normalTripStops = this.tripStops.filter((tripStop) => !tripStop.isReload);

    return new Map(normalTripStops.map((tripStop) => [tripStop.orderStopId, tripStop]));
  }

  get routeFreights(): TripRouteFreight[] {
    return this.routes.flatMap((route) => route.freights);
  }

  get activeRouteFreights(): TripRouteFreight[] {
    return this.activeRoutes.flatMap((route) => route.freights.filter((freight) => !freight.isDeleted));
  }

  get activeRouteStops(): TripRouteStop[] {
    return this.activeRoutes.flatMap((route) => route.routeStops.filter((routeStop) => isRouteStopActive(routeStop)));
  }

  get shouldShowReorderStopsDialog(): boolean {
    const hasNewOrderStop = !this.orderStops.every((orderStop) => this.tripStopsByOrderStopId.get(orderStop.id));

    return hasNewOrderStop;
  }

  get travelOrdersByOrder(): Record<string, TravelOrderDto[]> {
    const travelOrders = this.trip.routes.flatMap((route) => route.travelOrders);
    return travelOrders.reduce((acc: Record<string, TravelOrderDto[]>, travelOrder) => {
      if (!acc[travelOrder.orderId]) {
        acc[travelOrder.orderId] = [travelOrder];
      } else {
        acc[travelOrder.orderId].push(travelOrder);
      }

      return acc;
    }, {});
  }

  get orderFreights(): TripOrderFreight[] {
    return this.orders
      .flatMap((order) => order.freights)
      .map((orderFreight) => {
        const label = getFreightOrdinalLabel(this.trip.tripStops, orderFreight.id);

        return {
          ...orderFreight,
          number: Number(label?.split("-")[1]) || 0,
          name: getFreightName(orderFreight, label, "", false),
        };
      });
  }

  get maxOrderStopPosition(): number {
    return Math.max(...this.orderStops.flatMap((orderStop) => orderStop.position));
  }

  private get isNewTrip(): boolean {
    return this.route.params.id === this.newTripSlug;
  }

  async load(): Promise<void> {
    if (this.isNewTrip) {
      return this.loadNewTrip();
    }

    return this.loadExistingTrip();
  }

  async loadTrip(tripId: number) {
    const { showSnackbarError } = useSnackbarStore();

    try {
      const resp = await this.api.trips.getTripById(tripId);
      if (!resp.success || !resp.data) {
        showSnackbarError("Trip was not found");
        return;
      }

      const trip: TripDto = resp.data;

      this.originalTrip = { ...trip };

      const orderIds = Array.from(new Set(trip.routes.flatMap((route) => route.travelOrders.map((to) => to.orderId))));

      const orders = (
        await Promise.all(
          orderIds.map((orderId) => {
            return this.loadTripOrder(orderId);
          }),
        )
      ).filter((order): order is TripOrder => Boolean(order));

      const truckIds = Array.from(new Set(trip.routes.flatMap((route) => route.truckId)));

      const trucks = (
        await Promise.all(
          truckIds.map((orderId) => {
            return this.loadTruck(orderId);
          }),
        )
      ).filter((truck): truck is TruckDto => Boolean(truck));

      const reloadTripStops = trip.tripStops.filter((tripStop) => isReloadStop(tripStop));

      const reloadFacilities = (await Promise.all(
        reloadTripStops.map((tripStop) => {
          return this.loadFacility(tripStop.facilityId);
        }),
      )) as FacilityDto[];

      this.trip = this.prepareTripForUI(trip, orders, trucks, reloadFacilities);
      this.trip.tripStops = this.recalculateTripStopsWithNumberAndName();
      this.updateRouteStopsPosition();
      this.setRouteStopFacility();
    } catch (error) {
      console.error(error);
    }
  }

  addOrderToTrip(order: TripOrder): void {
    const newOrder: TripOrder = {
      ...order,
      orderStops: order.orderStops.map((orderStop) => {
        return {
          ...orderStop,
          position: this.maxOrderStopPosition + orderStop.position,
        };
      }),
    };

    this.trip = {
      ...this.trip,
      orders: [...this.trip.orders, newOrder],
      routes: this.trip.routes.map((route, routeIndex) => {
        const newRouteFreights = order.freights.map((orderFreight) => {
          return createRouteFreightFromOrderFreight(orderFreight);
        });

        return {
          ...route,
          freights: [...route.freights, ...newRouteFreights],
          travelOrders: [...route.travelOrders, this.getTravelOrderFromOrder(order, routeIndex)],
        };
      }),
    };
  }

  removeOrder(order: TripOrder): void {
    this.trip = {
      tripStops: this.tripStops
        .filter((tripStop) => {
          return tripStop.order?.id !== order.id;
        })
        .map((tripStop, index) => {
          return {
            ...tripStop,
            position: index + 1,
          };
        }),
      orders: this.orders.filter((item) => {
        return item.id !== order.id;
      }),
      routes: this.routes.map((route) => {
        return {
          ...route,
          travelOrders: route.travelOrders.filter((travelOrder) => travelOrder.orderId !== order.id),
          routeStops: route.routeStops.filter((routeStop) => {
            if (!routeStop.order) {
              return true;
            }

            return routeStop.order.id !== order.id;
          }),
        };
      }),
    };

    this.trip.tripStops = this.recalculateTripStopsWithNumberAndName();
    this.updateRouteStopsPosition();
  }

  updateFreights(updatedRoute: TripRoute, updatedFreights: TripRouteFreight[], updatedRouteStop: TripRouteStop) {
    const updateFreightsById = new Map(updatedFreights.map((f) => [getEntityKey(f), f]));
    const updatedTrip = {
      ...this.trip,
      routes: this.routes.map((route) => {
        if (!isSameEntity(route, updatedRoute)) {
          return route;
        }

        const merged = route.freights
          .filter((freight) => {
            return updateFreightsById.has(getEntityKey(freight));
          })
          .map((f) =>
            updateFreightsById.has(getEntityKey(f)) ? { ...f, ...updateFreightsById.get(getEntityKey(f))! } : f,
          );

        const newOnes = updatedFreights.filter((f) => !route.freights.some((rf) => isSameEntity(rf, f)));
        const updatedRouteStops = route.routeStops.map((routeStop) => {
          return {
            ...routeStop,
            freights: routeStop.freights.filter((freight) => updateFreightsById.has(freight)),
          };
        });

        return { ...route, freights: [...merged, ...newOnes], routeStops: updatedRouteStops };
      }),
      tripStops: this.tripStops.map((tripStop) => {
        if (!isReloadStop(tripStop)) {
          return tripStop;
        }

        const updatedReloadFreights = tripStop.reloadFreights?.map((reloadFreight) => {
          const matchedFreight = updateFreightsById.get(getEntityKey(reloadFreight));

          if (!matchedFreight) {
            return reloadFreight;
          }

          return matchedFreight;
        });

        return {
          ...tripStop,
          reloadFreights: updatedReloadFreights,
        };
      }),
    };

    const deletedIds = updatedTrip.routes
      .flatMap((route) => route.freights)
      .filter((f) => f.isDeleted)
      .map((f) => getEntityKey(f));

    this.trip = {
      ...updatedTrip,
      routes: updatedTrip.routes.map((route) => {
        return {
          ...route,
          freights: route.freights.map((freight) => {
            let shouldDelete = freight.isDeleted;

            if (freight.parentFreightId && deletedIds.includes(freight.parentFreightId)) {
              shouldDelete = true;
            }

            const hasDeletedPickUp = updatedRouteStop.type === OrderStopType.pickup && updatedRouteStop.isDeleted;
            const hasMatchingFreight = route.freights.some((f) => updatedRouteStop.freights?.includes(getEntityKey(f)));
            const activePickups = route.routeStops.filter(
              (stop) => stop.type === OrderStopType.pickup && !stop.isDeleted,
            );

            if (hasMatchingFreight && hasDeletedPickUp && !activePickups.length) {
              shouldDelete = true;
            }

            return {
              ...freight,
              isDeleted: shouldDelete,
            };
          }),
        };
      }),
    };
  }

  updateTravelOrderTempIds() {
    this.trip.routes = this.trip.routes.map((route) => {
      return {
        ...route,
        routeStops: route.routeStops.map((routeStop) => {
          const stopWithTravelOrders = {
            ...routeStop,
            travelOrderTempIds: this.setRouteStopTravelOrderTempIds(route, routeStop, this.orders),
          };

          return {
            ...stopWithTravelOrders,
            $isCancelled: this.checkIsRouteStopCanceled(stopWithTravelOrders, route.travelOrders),
          };
        }),
      };
    });
  }

  editTravelOrder(travelOrderToEdit: TravelOrderDto) {
    this.trip.routes = this.trip.routes.map((route) => {
      return {
        ...route,
        travelOrders: route.travelOrders.map((to) => {
          if (to.orderId === travelOrderToEdit.orderId) {
            return {
              ...to,
              order: {
                ...to.order,
                status: travelOrderToEdit.order.status,
                rate: travelOrderToEdit.order.rate,
              },
            };
          }

          return to;
        }),
      };
    });
  }

  editRouteStopsByTravelOrder(travelOrder: TravelOrderDto) {
    this.trip.routes = this.trip.routes.map((route) => {
      return {
        ...route,
        routeStops: route.routeStops.map((routeStop) => {
          if (!travelOrder.tempId || !routeStop.travelOrderTempIds?.includes(travelOrder.tempId)) {
            return routeStop;
          }

          const $isCancelled = this.checkIsRouteStopCanceled(routeStop, route.travelOrders);
          const currentFacility = $isCancelled && routeStop.oldFacility ? routeStop.oldFacility : routeStop.facility;
          const currentFacilityId =
            $isCancelled && routeStop.oldFacilityId ? routeStop.oldFacilityId : routeStop.facilityId;

          return {
            ...routeStop,
            $isCancelled,
            facility: currentFacility,
            facilityId: currentFacilityId,
          };
        }),
      };
    });
  }

  editOrderByTravelOrder(travelOrder: TravelOrderDto) {
    const order = this.orders.find((order) => order.id === travelOrder.orderId);

    if (order) {
      order.rate = travelOrder.order.rate;
      order.status = travelOrder.order.status;
    }
  }

  updateRouteStatus(updatedRoute: TripRoute) {
    const originalRoute = this.originalTrip?.routes.find((originalRoute) => originalRoute.id === updatedRoute.id);

    this.trip.routes = this.trip.routes.map((route) => {
      if (isSameEntity(route, updatedRoute)) {
        const isAllTOCancelled = updatedRoute.travelOrders.every((to) =>
          TravelOrderCancellationStatusMap.find((cancelTO) => cancelTO.id === to.status),
        );

        const prevStatus = originalRoute?.status || RouteStatus.notStarted;

        return {
          ...route,
          status: isAllTOCancelled ? RouteStatus.cancelled : prevStatus,
        };
      }

      return route;
    });
  }

  updateRouteStop(updatedRouteStop: TripRouteStop) {
    this.trip = {
      ...this.trip,
      routes: this.trip.routes.map((route) => {
        return {
          ...route,
          routeStops: route.routeStops.map((routeStop) => {
            const hasMatch = isSameEntity(updatedRouteStop, routeStop);

            if (hasMatch) {
              const namePrefix = updatedRouteStop.isReload ? "Reload" : "";

              return {
                ...updatedRouteStop,
                name: createStopName(updatedRouteStop.type, updatedRouteStop.number, {
                  prefix: namePrefix,
                }),
              };
            }

            return routeStop;
          }),
        };
      }),
    };
  }

  prepareTripForAPI(trip: Trip, id?: number): TripDto {
    const preparedRoutes: TripRouteDto[] = trip.routes.map((route) => {
      const { tempId, travelOrders, ...routeInfo } = route;

      const preparedFreights = routeInfo.freights.map((freight) => {
        const { splitFreightId, $existedUIOnly, ...freightInfo } = freight;

        return freightInfo;
      });

      const preparedTravelOrders = travelOrders.map((to) => {
        const { tempId, ...toInfo } = to;
        return {
          ...toInfo,
          payments: toInfo.payments.map((payment) => {
            const { tempId, ...paymentInfo } = payment;

            return {
              ...paymentInfo,
            };
          }),
        };
      });

      return {
        ...routeInfo,
        freights: preparedFreights,
        travelOrders: preparedTravelOrders,
        routeStops: route.routeStops
          .filter((routeStop) => {
            if (routeStop.id) {
              return routeStop.use;
            }

            return !routeStop.isDeleted && routeStop.use;
          })

          .map(({ tempId, use, $isCancelled, ...rest }) => rest)
          .sort((a, b) => a.position - b.position),
      };
    });

    const preparedTripStops: TripStopDto[] = trip.tripStops.map((tripStop) => {
      return {
        id: tripStop.id,
        address: tripStop.address,
        type: tripStop.type,
        note: tripStop.note,
        position: tripStop.position,
        facilityId: tripStop.facilityId,
        orderStopId: tripStop.orderStopId,
        isReload: tripStop.isReload,
        timezone: tripStop.timezone,
        timeTo: tripStop.timeTo,
        timeFrom: tripStop.timeFrom,
        timeType: tripStop.timeType,
        isDeleted: tripStop.isDeleted,
      };
    });

    return {
      id,
      routes: preparedRoutes,
      tripStops: preparedTripStops,
    };
  }

  async loadTripOrder(orderId: string): Promise<TripOrder | undefined> {
    try {
      const resp = await this.api.orders.findOrderById(orderId);
      if (!resp.success || !resp.data) {
        return;
      }

      const baseOrder: OrderDto = {
        ...resp.data,
      };

      const fileResp = await this.api.orders.findOrderFiles(baseOrder.id!);
      const files = fileResp.success && fileResp.data ? fileResp.data : [];

      const freights = baseOrder.freights;

      const orderStops: TripOrderStop[] = await Promise.all(
        baseOrder.orderStops.map(async (stop) => {
          const stopWithOrderId = {
            ...stop,
            orderId: baseOrder.id,
          };

          if (stop.oldFacilityId) {
            const facilityResp = await this.api.company.findFacilityById(stop.oldFacilityId);

            if (!facilityResp.success || !facilityResp.data) {
              return {
                ...stopWithOrderId,
              };
            }

            return {
              ...stopWithOrderId,
              oldFacility: facilityResp.data,
            };
          }

          return { ...stopWithOrderId };
        }),
      );

      const order: TripOrder = {
        ...baseOrder,
        files,
        freights,
        orderStops,
      };

      return order;
    } catch (error) {
      console.error(`Failed to load order ${orderId}:`, error);
      return undefined;
    }
  }

  addReloadStop(stop: TripStop) {
    const reloadTripStop = this.buildTripReloadStop(stop, {
      facility: stop.facility,
    });

    const updatedTripStops = this.tripStops.map((tripStop) => {
      if (tripStop.position >= reloadTripStop.position) {
        return {
          ...tripStop,
          position: tripStop.position + 1,
        };
      }

      return tripStop;
    });

    this.trip.tripStops = [...updatedTripStops, reloadTripStop];

    this.trip.tripStops = this.recalculateTripStopsWithNumberAndName();

    const shouldCreateBasicSplitFlow =
      this.activeRoutes.length === 1 && this.activeRouteFreights.length === 1 && this.activeRouteStops.length === 2;

    if (shouldCreateBasicSplitFlow) {
      this.createBasicSplitFlow(reloadTripStop);
    } else {
      const reloadRouteStop = this.buildRouteStopFromTripStop(reloadTripStop, null, [], {
        type: OrderStopType.notSelected,
      });

      this.trip.routes = this.trip.routes.map((route) => {
        return {
          ...route,
          routeStops: [
            ...route.routeStops,
            {
              ...reloadRouteStop,
              tempId: generateId(),
            },
          ],
        };
      });
    }

    this.updateRouteStopsPosition();
  }

  addReloadFreights(routeStop: TripRouteStop) {
    if (!routeStop.isReload || !routeStop.freights?.length || routeStop.type !== OrderStopType.delivery) {
      return;
    }

    const selectedRouteFreights: TripRouteFreight[] = this.routeFreights.filter((routeFreight) =>
      routeStop.freights!.includes(getEntityKey(routeFreight)),
    );

    if (!selectedRouteFreights.length) {
      return;
    }

    this.trip.tripStops = this.tripStops.map((tripStop) => {
      if (routeStop.tripStopId === tripStop.id && isReloadStop(tripStop)) {
        const existingFreights = tripStop.reloadFreights || [];
        const currentLength = tripStop.reloadFreights?.length || 0;
        const existingParentIds = new Set(existingFreights.map((f) => getEntityKey(f)));

        const newReloadFreights = selectedRouteFreights
          .filter((freight) => !existingParentIds.has(getEntityKey(freight)))
          .map((selectedRouteFreight, index) => {
            const number = currentLength + index + 1;

            return {
              ...selectedRouteFreight,
              number,
            };
          });

        return {
          ...tripStop,
          reloadFreights: [...existingFreights, ...newReloadFreights],
        };
      }

      return tripStop;
    });
  }

  createTripStopFromOrderStop(
    orderStop: TripOrderStop,
    order: TripOrder,
    options: {
      id?: string;
      position?: number;
    } = {},
  ): TripStop {
    const number = createStopNumber(orderStop.id, this.orderStops);
    const { id = generateId(), position = orderStop.position } = options;

    const tripStop: TripStop = {
      id,
      type: orderStop.type,
      facilityId: orderStop.facilityId,
      address: orderStop.facility?.fullAddress || orderStop.facility?.address || "",
      position,
      orderStopId: orderStop.id || null,
      note: orderStop.note || "",
      freights: orderStop.freights || [],
      isDeleted: orderStop.isDeleted,
      state: orderStop.state,
      lastEditedBy: orderStop.lastEditedBy,
      facility: orderStop.facility,
      oldFacilityId: orderStop.oldFacilityId,
      order,
      timeType: orderStop.timeType,
      timeFrom: orderStop.timeFrom,
      timeTo: orderStop.timeTo,
      time2From: orderStop.time2From,
      time2To: orderStop.time2To,
      time2Type: orderStop.time2Type,
      timezone: orderStop.timezone,
      status: orderStop.status,
      number,
      oldFacility: orderStop.oldFacility,
      isReload: false,
      name: createStopName(orderStop.type, number),
    };

    return tripStop;
  }

  reorderTripStops(tripStops: TripStop[]) {
    this.trip = {
      ...this.trip,
      tripStops,
      routes: this.trip.routes.map((route) => {
        return {
          ...route,
          routeStops: tripStops.map((tripStop) => {
            const existedRouteStop = route.routeStops.find((routeStop) => routeStop.tripStopId === tripStop.id);
            if (existedRouteStop) {
              const isStopCanceled = this.checkIsRouteStopCanceled(existedRouteStop, route.travelOrders);
              const isProcessStarted = !NotStartedRouteStopStatus.includes(existedRouteStop.status);
              const shouldUpdateTimeFields = !isStopCanceled && !isProcessStarted;

              return {
                ...existedRouteStop,
                position: tripStop.position,

                ...(shouldUpdateTimeFields && {
                  timeTo: tripStop.timeTo,
                  timeFrom: tripStop.timeFrom,
                  timeType: tripStop.timeType,
                  timezone: tripStop.timezone,
                }),

                ...(shouldUpdateTimeFields &&
                  !tripStop.isReload && {
                    time2To: tripStop.time2To,
                    time2From: tripStop.time2From,
                    time2Type: tripStop.time2Type,
                  }),
              };
            }

            const orderId = tripStop.order?.id;
            const hasOrderInTrip = route.routeStops.some(
              (existingRouteStop) => existingRouteStop.order?.id === orderId,
            );
            // If we just add new stop to existed order we don`t need to set route freights for new route stop automatically
            const routeFreightsForNewRouteStop = hasOrderInTrip ? [] : this.routeFreights;

            const routeStop = this.buildRouteStopFromTripStop(tripStop, null, routeFreightsForNewRouteStop, {
              use: true,
            });

            return {
              ...routeStop,
              travelOrderTempIds: this.setRouteStopTravelOrderTempIds(route, routeStop, this.orders),
            };
          }),
        };
      }),
    };
  }

  setRouteStopTravelOrderTempIds(
    route: TripRoute | TripRouteDto,
    routeStop: TripRouteStop | RouteStopDto,
    orders: TripOrder[],
  ): string[] {
    const freightIds = route.freights
      .filter((routeFreight) => routeStop.freights.includes(getEntityKey(routeFreight)))
      .map((routeFreight) => routeFreight.freightId);

    const relatedOrderIds = orders
      .filter((order) => order.freights.some((freight) => freight.id && freightIds.includes(freight.id)))
      .map((order) => order.id);

    const relatedTravelOrders = route.travelOrders.filter(
      (travelOrder) => !!travelOrder.tempId && relatedOrderIds.includes(travelOrder.orderId),
    );

    return relatedTravelOrders.map((travelOrder) => travelOrder.tempId!);
  }

  createInitialRouteStopFreights(
    tripStop: TripStop,
    freights: string[] | null = null,
    routeFreights: TripRouteFreight[] = this.routeFreights,
  ): string[] {
    if (freights) {
      return freights;
    }

    if (isReloadStop(tripStop)) {
      return tripStop.reloadFreights?.map((freight) => getEntityKey(freight)).filter(Boolean) || [];
    }

    const freightIdMap = new Map(routeFreights.map((rf) => [rf.freightId, getEntityKey(rf)]));
    return tripStop.freights.map((freight) => freightIdMap.get(freight)).filter(Boolean) as string[];
  }

  createRouteFreights(route: TripRoute, copyFreights = true): TripRouteFreight[] {
    const deliveries = route.routeStops.filter((routeStop) => routeStop.type === OrderStopType.delivery);
    const hasOneOrderFreight = this.orderFreights.length === 1;

    if (hasOneOrderFreight && !this.activeRoutes.length) {
      return this.orderFreights.flatMap((orderFreight) => {
        const dividedWeight = orderFreight.weight ? Math.floor(orderFreight.weight / deliveries.length) : null;
        const dividedQuantity = orderFreight.quantity ? Math.floor(orderFreight.quantity / deliveries.length) : null;

        return deliveries.map((_, index) => {
          return createRouteFreightFromOrderFreight(orderFreight, {
            index,
            quantity: dividedQuantity,
            weight: dividedWeight,
          });
        });
      });
    }

    const routeFreightsFromOrderFreights = this.orderFreights.map((orderFreight, index) => {
      return createRouteFreightFromOrderFreight(orderFreight, {
        index,
      });
    });

    if (copyFreights) {
      return routeFreightsFromOrderFreights;
    }

    return [];
  }

  buildRouteStopFromTripStop(
    tripStop: TripStop,
    routeStop: RouteStopDto | TripRouteStop | null = null,
    routeFreights: TripRouteFreight[] = this.routeFreights,
    options: {
      use?: boolean;
      type?: OrderStopType;
    } = {},
  ): TripRouteStop {
    const { use = false, type = routeStop?.type } = options;

    return {
      id: routeStop?.id,
      bolFiles: routeStop?.bolFiles || [],
      checkInTime: routeStop?.checkInTime || null,
      checkOutTime: routeStop?.checkOutTime || null,
      loadUnloadTime: routeStop?.loadUnloadTime || null,
      number: tripStop.number,
      name: this.createRouteStopName(routeStop, tripStop),
      oldFacilityId: tripStop.oldFacilityId || null,
      oldFacility: tripStop.oldFacility || null,
      otherFiles: routeStop?.otherFiles || [],
      podFiles: routeStop?.podFiles || [],
      podSignedBy: routeStop?.podSignedBy || null,
      position: tripStop.position,
      state: routeStop?.state || RouteStopState.normal,
      verifyDispatcherId: routeStop?.verifyDispatcherId || null,
      verifyTime: routeStop?.verifyTime || null,
      tripStopId: tripStop.id,
      orderStopId: tripStop.orderStopId,
      order: !isReloadStop(tripStop)
        ? {
            id: tripStop.order!.id!,
            number: tripStop.order!.number!,
            status: tripStop.order!.status!,
            rate: tripStop.order?.rate || 0,
          }
        : null,
      type: type || tripStop.type,
      facility: routeStop?.facility || tripStop.facility,
      facilityId: routeStop?.facilityId || tripStop.facilityId,
      freights: this.createInitialRouteStopFreights(tripStop, routeStop?.freights, routeFreights),
      editable: this.trip.routes.length > 1,
      isReload: tripStop.isReload || false,
      use,
      status: routeStop?.status || RouteStopStatus.notStarted,
      note: routeStop?.note || tripStop.note,
      timezone: routeStop?.timezone || tripStop.timezone,
      timeType: routeStop?.timeType || tripStop.timeType,
      timeFrom: routeStop?.timeFrom || tripStop.timeFrom,
      timeTo: routeStop?.timeTo || tripStop.timeTo,
      time2Type: routeStop?.time2Type || tripStop.time2Type,
      time2From: routeStop?.time2From || tripStop.time2From,
      time2To: routeStop?.time2To || tripStop.time2To,
      tempId: generateId(),
      isDeleted: tripStop.isDeleted || Boolean(routeStop?.isDeleted),
      travelOrderTempIds: [],
      bols: [],
    };
  }

  addRoute(truck: TruckDto | undefined = undefined) {
    const route: TripRoute = {
      distances: {},
      deadheadDistance: null,
      freights: [],
      bols: [],
      redispatch: RouteRedispatchState.normal,
      status: RouteStatus.notStarted,
      totalDistance: 0,
      truck: truck,
      truckId: truck?.id,
      tempId: generateId(),
      isDispatched: false,
      wasCancelled: false,
      wrongDeadheadDistance: false,
      technicalPartial: false,
      routeStops: this.tripStops.map((tripStop) => {
        return this.buildRouteStopFromTripStop(tripStop, null, [], {
          use: !this.activeRoutes.length,
          type: tripStop.isReload ? OrderStopType.notSelected : tripStop.type,
        });
      }),
      travelOrders: this.createRouteTravelOrders(),
      driverId: null,
      driver2Id: null,
      ownerId: null,
      ownerCompanyId: null,
    };

    const routeFreights = this.createRouteFreights(route, !this.activeRoutes.length);

    route.freights = routeFreights;

    // First we create stop freights
    route.routeStops = route.routeStops.map((routeStop) => {
      return {
        ...routeStop,
        freights: this.getRouteStopFreights(route, routeStop, routeFreights),
      };
    });

    // Second we need stop freights to create travelOrderTempIds
    route.routeStops = route.routeStops.map((routeStop) => {
      return {
        ...routeStop,
        travelOrderTempIds: this.setRouteStopTravelOrderTempIds(route, routeStop, this.orders),
      };
    });

    this.trip.routes.push(route);
  }

  recalculateTripStopsWithNumberAndName(tripStops: TripStop[] = this.trip.tripStops): TripStop[] {
    return tripStops.map((tripStop) => {
      const number = createStopNumber(tripStop.id, tripStops);

      return {
        ...tripStop,
        number,
        name: createStopName(tripStop.type, number, {
          suffix: isReloadStop(tripStop) ? "stop" : "",
        }),
      };
    });
  }

  private checkIsRouteStopCanceled(routeStop: TripRouteStop, travelOrders: TravelOrderDto[]): boolean {
    const relatedTravelOrders = travelOrders.filter((to) => {
      return to?.tempId && routeStop.travelOrderTempIds?.includes(to.tempId);
    });

    return Boolean(relatedTravelOrders.length && relatedTravelOrders.every((to) => isTravelOrderCancelled(to)));
  }

  private createBasicSplitFlow(reloadTripStop: TripStop) {
    const [route1] = this.activeRoutes;
    const route1Freight = route1.freights.find((freight) => !freight.isDeleted);

    if (!route1Freight) {
      return;
    }

    const relatedOrderFreight = this.orderFreights.find((of) => of.id === route1Freight.freightId);

    if (!relatedOrderFreight) {
      return;
    }

    const reloadDeliveryForRoute1 = this.buildRouteStopFromTripStop(reloadTripStop, null, route1.freights, {
      use: true,
      type: OrderStopType.delivery,
    });

    reloadDeliveryForRoute1.freights = [getEntityKey(route1Freight)];

    const route1NewRouteStops = [
      ...route1.routeStops.map((routeStop) => {
        if (routeStop.type === OrderStopType.delivery && !routeStop.isReload) {
          return {
            ...routeStop,
            isDeleted: true,
            use: !this.isNewTrip,
          };
        }

        return routeStop;
      }),
      reloadDeliveryForRoute1,
    ];

    const route1Index = this.trip.routes.findIndex((route) => isSameEntity(route, route1));

    this.trip.routes[route1Index] = {
      ...route1,
      routeStops: route1NewRouteStops,
    };

    this.addReloadFreights(reloadDeliveryForRoute1);

    this.addRoute();

    const route2 = this.trip.routes[this.trip.routes.length - 1]; // always the route added ahead
    const route2Freight: TripRouteFreight = {
      ...createRouteFreightFromOrderFreight(relatedOrderFreight, { index: 0 }),
      parentFreightId: getEntityKey(route1Freight),
    };

    route2.freights = [route2Freight];
    route2.routeStops = route2.routeStops.map((routeStop) => {
      if (routeStop.tripStopId === reloadTripStop.id) {
        const pickUpReloadStop = {
          ...routeStop,
          type: OrderStopType.pickup,
          freights: [getEntityKey(route2Freight)],
          use: true,
        };

        return pickUpReloadStop;
      }

      const tripStop = this.tripStopsById.get(routeStop.tripStopId);

      if (tripStop && !tripStop.isReload && tripStop.type === OrderStopType.delivery) {
        const deliveryStopWithFreights = {
          ...routeStop,
          freights: [getEntityKey(route2Freight)],
          use: true,
        };

        return deliveryStopWithFreights;
      }

      return routeStop;
    });

    route2.routeStops = route2.routeStops.map((routeStop) => {
      return {
        ...routeStop,
        travelOrderTempIds: this.setRouteStopTravelOrderTempIds(route2, routeStop, this.orders),
      };
    });
  }

  private getRouteStopsByTripStopId(routeStops: (TripRouteStop | RouteStopDto)[], tripStopId: string) {
    return routeStops.filter((routeStop) => routeStop.tripStopId === tripStopId);
  }

  private buildTripReloadStop(
    reloadTripStop: TripStopDto,
    options: {
      facility: FacilityDto;
      reloadFreights?: TripRouteFreight[];
    },
  ): TripStop {
    const { facility, reloadFreights = [] } = options;

    return {
      freights: [],
      isDeleted: false,
      number: 0,
      name: "",
      status: OrderStopStatus.notStarted,
      facility,
      reloadFreights,
      ...reloadTripStop,
    };
  }

  private prepareTripForUI(
    tripDto: TripDto,
    orders: TripOrder[],
    trucks: TruckDto[],
    reloadFacilities: FacilityDto[],
  ): Trip {
    const ordersFreights = orders.flatMap((order) => order.freights);

    const tripStops = tripDto.tripStops;
    const routeFreights = tripDto.routes.flatMap((route) => route.freights);

    const cancelledTravelOrderIds = new Set(
      tripDto.routes
        .flatMap((route) => route.travelOrders)
        .filter((travelOrder) => isTravelOrderCancelled(travelOrder))
        .map((travelOrder) => travelOrder.id),
    );

    const activeRouteFreightIds = new Set(
      tripDto.routes
        .flatMap((route) => route.routeStops)
        .filter((routeStop) => routeStop.travelOrderIds?.some((id) => !cancelledTravelOrderIds.has(id)))
        .flatMap((routeStop) => routeStop.freights),
    );

    const trucksById = new Map(trucks.map((truck) => [truck.id, truck]));

    const preparedTripStops: TripStop[] = this.prepareTripStopsForUI(
      tripStops,
      orders,
      tripDto.routes,
      reloadFacilities,
    );

    const preparedRoutes: TripRoute[] = tripDto.routes.map((route, routeIndex) => {
      // first we prepare route for UI
      const preparedRoute = {
        ...route,
        tempId: generateId(),
        travelOrders: route.travelOrders.map((to) => this.prepareTravelOrderForUI(to, routeIndex)),
        routeStops: preparedTripStops.map((tripStop) => this.prepareRouteStopForUI(tripStop, route.routeStops)),
        freights: route.freights.map((freight) =>
          this.prepareRouteFreightForUI(freight, ordersFreights, routeFreights, activeRouteFreightIds),
        ),
        isDispatched: route.redispatch !== RouteRedispatchState.needed,
        wasCancelled: false,
        truck: route.truckId ? trucksById.get(route.truckId) : undefined,
      };

      // then set travel order ids to route stop
      preparedRoute.routeStops = preparedRoute.routeStops.map((routeStop) => {
        const stopWithTravelOrders = {
          ...routeStop,
          travelOrderTempIds: this.setRouteStopTravelOrderTempIds(preparedRoute, routeStop, orders),
        };

        return {
          ...stopWithTravelOrders,
          $isCancelled: this.checkIsRouteStopCanceled(stopWithTravelOrders, preparedRoute.travelOrders),
        };
      });

      return preparedRoute;
    });

    return {
      routes: preparedRoutes,
      tripStops: preparedTripStops,
      orders,
    };
  }

  private createRouteTravelOrders(): TravelOrderDto[] {
    return this.orders.map((order) => {
      return this.getTravelOrderFromOrder(order);
    });
  }

  private getRouteStopFreights(
    route: TripRoute,
    routeStop: TripRouteStop,
    routeFreights: TripRouteFreight[],
  ): string[] {
    const tripStop = this.tripStops.find((tripStop) => tripStop.id === routeStop.tripStopId);

    const relatedRouteFreights = routeFreights.filter((routeFreight) =>
      tripStop?.freights.includes(routeFreight.freightId),
    );

    if (!relatedRouteFreights.length) {
      return [];
    }

    if (routeStop.type === OrderStopType.pickup || this.orderFreights.length > 1) {
      return relatedRouteFreights.map((freight) => getEntityKey(freight));
    }

    const deliveryIndex = route.routeStops
      .filter((routeStop) => routeStop.type === OrderStopType.delivery)
      .findIndex((delivery) => isSameEntity(delivery, routeStop));

    if (deliveryIndex === -1) {
      return [];
    }

    return [getEntityKey(relatedRouteFreights[deliveryIndex])];
  }

  private sortRouteStops() {
    this.trip.routes = this.trip.routes.map((route) => {
      return {
        ...route,
        routeStops: [...route.routeStops].sort((a, b) => a.position - b.position),
      };
    });
  }

  private updateRouteStopsPosition() {
    this.trip.routes = this.trip.routes.map((route) => {
      return {
        ...route,
        routeStops: route.routeStops.map((routeStop) => {
          const relatedTripStop = this.tripStopsById.get(routeStop.tripStopId);

          if (!relatedTripStop) {
            return {
              ...routeStop,
            };
          }

          return {
            ...routeStop,
            position: relatedTripStop.position,
            number: relatedTripStop.number,
            name: this.createRouteStopName(routeStop, relatedTripStop),
          };
        }),
      };
    });

    this.sortRouteStops();
  }

  private setRouteStopFacility() {
    this.trip.routes = this.trip.routes.map((route) => {
      return {
        ...route,
        routeStops: route.routeStops.map((routeStop) => {
          const relatedTripStop = this.tripStopsById.get(routeStop.tripStopId);
          if (!relatedTripStop) {
            return routeStop;
          }

          const isRouteStopCanceled = this.checkIsRouteStopCanceled(routeStop, route.travelOrders);
          const isProcessStarted = checkIsRouteStopProcessStarted(routeStop);

          if (!isRouteStopCanceled && !isProcessStarted) {
            return {
              ...routeStop,
              facility: relatedTripStop.facility,
              facilityId: relatedTripStop.facilityId,
            };
          }

          return routeStop;
        }),
      };
    });
  }

  private prepareRouteStopForUI(tripStop: TripStop, routeStops: RouteStopDto[]): TripRouteStop {
    const relatedRouteStop = routeStops.find((item) => item.tripStopId === tripStop.id) as RouteStopDto;

    return this.buildRouteStopFromTripStop(tripStop, relatedRouteStop, this.routeFreights, {
      use: Boolean(relatedRouteStop),
    });
  }

  private prepareRouteFreightForUI(
    routeFreight: TripRouteFreight,
    orderFreights: OrderFreightDto[],
    routeFreights: TripRouteFreight[],
    activeRouteFreightIds: Set<string>,
  ): TripRouteFreight {
    const isBasedOnOrderFreight = !routeFreight.parentFreightId;

    if (!isBasedOnOrderFreight) {
      return routeFreight;
    }

    const orderFreightsById = new Map(orderFreights.map((freight) => [freight.id, freight]));

    const orderFreight = orderFreightsById.get(routeFreight.freightId);

    const routeFreightsBasedOnOrderFreight = routeFreights.filter(
      (routeFreight) =>
        routeFreight.freightId === orderFreight?.id && activeRouteFreightIds.has(getEntityKey(routeFreight)),
    );

    const [activeRouteFreight] = routeFreightsBasedOnOrderFreight;

    const shouldInheritAll =
      routeFreightsBasedOnOrderFreight.length === 1 &&
      Boolean(activeRouteFreight) &&
      isSameEntity(activeRouteFreight, routeFreight);

    if (shouldInheritAll && orderFreight) {
      return {
        ...routeFreight,
        type: orderFreight.type ?? routeFreight.type,
        weight: orderFreight.weight,
        quantity: orderFreight.quantity,
        plannedWeight: orderFreight.weight,
        plannedQuantity: orderFreight.quantity,
        length: orderFreight.length,
        width: orderFreight.width,
        height: orderFreight.height,
        stackable: orderFreight.stackable,
        state: orderFreight.state || routeFreight.state,
        isDeleted: orderFreight.isDeleted || routeFreight.isDeleted,
      };
    }

    return {
      ...routeFreight,
      type: orderFreight?.type || routeFreight.type,
      state: orderFreight?.state || routeFreight.state,
      isDeleted: orderFreight?.isDeleted || routeFreight.isDeleted,
    };
  }

  private prepareTravelOrderForUI(to: TravelOrderDto, routeIndex: number): TravelOrderDto {
    const mandatoryPayments = to.payments.filter(
      (payment) => payment.type && MandatoryPaymentTypes.includes(payment.type),
    );

    const optionalPayments = to.payments.filter(
      (payment) => !payment.type || !MandatoryPaymentTypes.includes(payment.type),
    );

    const sortedPayments = [...mandatoryPayments, ...optionalPayments].map((payment) => {
      return {
        ...payment,
        tempId: generateId(),
      };
    });

    return {
      ...to,
      tempId: generateId(),
      name: createTOName(routeIndex, to.order.number),
      payments: sortedPayments,
    };
  }

  private prepareTripStopsForUI(
    tripStops: TripStopDto[],
    orders: TripOrder[],
    routes: TripRouteDto[],
    reloadFacilities: FacilityDto[],
  ): TripStop[] {
    const reloadFacilityById = new Map(reloadFacilities.map((facility) => [facility.id, facility]));
    const orderStops = orders.flatMap((order) => order.orderStops);
    const routeStops = routes.flatMap((route) => route.routeStops);
    const routeFreights = routes.flatMap((route) => route.freights);

    const normalTripStops = tripStops
      .filter((tripStop) => !tripStop.isReload)
      .map((tripStop) => {
        const relatedOrderStop = orderStops.find((orderStop) => orderStop.id === tripStop.orderStopId) as TripOrderStop;
        const order = orders.find((order) => order.id === relatedOrderStop?.orderId) as TripOrder;

        return this.createTripStopFromOrderStop(relatedOrderStop, order, {
          id: tripStop.id,
          position: tripStop.position,
        });
      });

    const reloadTripStops = tripStops
      .filter((tripStop) => tripStop.isReload)
      .map((tripStop) => {
        const relatedRouteReloadStops = this.getRouteStopsByTripStopId(routeStops, tripStop.id);
        const relatedDeliveryRouteStops = relatedRouteReloadStops.filter((routeStop) => {
          return routeStop.type === OrderStopType.delivery;
        });
        const deliveryFreights = relatedDeliveryRouteStops.flatMap((routeStop) => routeStop.freights);
        const reloadFreights = routeFreights
          .filter((freight) => {
            return deliveryFreights.includes(getEntityKey(freight));
          })
          .map((routeFreight, index) => {
            const number = index + 1;
            return {
              ...routeFreight,
              number,
              name: getFreightName(routeFreight, number, "", false, true),
            };
          });

        const facility = reloadFacilityById.get(tripStop.facilityId)!;

        return this.buildTripReloadStop(tripStop, {
          facility: facility,
          reloadFreights,
        });
      });

    return [...normalTripStops, ...reloadTripStops];
  }

  private async loadNewTrip(): Promise<void> {
    if (!this.route.query.order || !this.route.query.truck) {
      throw new Error("Missing order or truck for new trip");
    }

    const { showSnackbarError } = useSnackbarStore();

    try {
      const order = await this.loadTripOrder(this.route.query!.order.toString());

      if (!order) {
        showSnackbarError("Order not found");
        return;
      }

      this.trip.orders.push(order);
      const tripStops = order.orderStops.map((orderStop) => {
        return this.createTripStopFromOrderStop(orderStop, order);
      });

      this.trip.tripStops = tripStops;

      const truck = await this.loadTruck(parseInt(this.route.query!.truck.toString()));

      if (!truck) {
        showSnackbarError("Truck not found");
        return;
      }

      this.addRoute(truck);
    } catch (error) {
      console.error(error);
    }
  }

  private async loadExistingTrip(): Promise<void> {
    if (!this.route.params.id) {
      throw new Error("Missing trip id");
    }

    await this.loadTrip(parseInt(this.route.params.id.toString()));
  }

  private async loadTruck(truckId: number | undefined): Promise<TruckDto | undefined> {
    if (!truckId) {
      return;
    }

    try {
      const resp = await this.api.trucks.findTruckById(truckId);

      if (!resp.success || !resp.data) {
        return;
      }

      return resp.data;
    } catch (error) {
      console.error(error);
    }
  }

  private async loadFacility(facilityId: number): Promise<FacilityDto | undefined> {
    try {
      const resp = await this.api.company.findFacilityById(facilityId);

      if (!resp.success || !resp.data) {
        return;
      }

      return resp.data;
    } catch (error) {
      console.error(error);
    }
  }

  private createRouteStopName(routeStop: RouteStopDto | TripRouteStop | null, tripStop: TripStop) {
    if (routeStop && routeStop.isReload) {
      return createStopName(routeStop.type, tripStop.number, {
        prefix: "Reload",
      });
    }

    return tripStop.name;
  }

  private getTravelOrderFromOrder(order: TripOrder, routeIndex: number = this.routes.length): TravelOrderDto {
    return {
      order: {
        billToCustomerId: order.billToCustomerId,
        bookedBy: order.bookedBy,
        id: order.id!,
        number: order.number!,
        rate: order.rate,
        status: order.status,
        refNumbers: order.refNumbers,
        temperatures: order.temperatures,
        protectFromFreezing: order.protectFromFreezing,
        customer: order.customer,
      },
      orderId: order.id!,
      status: TravelOrderStatus.active,
      updateInterval: null,
      lastUpdateTime: null,
      nextUpdateTime: null,
      name: createTOName(routeIndex, order.number),
      tempId: generateId(),
      payments: [
        {
          type: TravelOrderPaymentType.FlatLinehaul,
          status: TravelOrderPaymentStatus.New,
          amount: null,
          tempId: generateId(),
        },
      ],
    };
  }
}

export default TripManager;
